<template>
  <ion-content class="text-selectable modal-content">
    <div class="content-wrapper">
      <div class="scrollable-content">
        <div class="info-section">
          <div class="company-info">
            <div class="company-logo">
              <img v-if="invoice.deal?.contractingCompany?.mediaLogo?.readPublicLink" :src="invoice.deal?.contractingCompany?.mediaLogo?.readPublicLink" :alt="invoice.deal?.brand?.name" />
              <div v-else class="company-logo-placeholder">
                <font-awesome-icon :icon="['fas', 'building']" />
              </div>
            </div>
            <div class="company-details">
              <h2 class="company-name" @click="navigateToDealDetails">{{ invoice.deal?.brand?.name }}</h2>
              <div class="company-subtitle">{{ t("common.invoice") }}</div>
            </div>
          </div>
        </div>

        <div class="status-section">
          <div class="status-card" :class="'status-card--' + (invoice?.paymentStatus && invoice?.status != 2 ? paymentStatus.color : status.color)">
            <div class="status-content">
              <div class="status-title">{{ invoice?.paymentStatus && invoice?.status != 2 ? paymentStatus.text : status.text }}</div>
              <div class="status-subtitle">{{ t("common.invoice-status") }}</div>
            </div>
          </div>
        </div>

        <div class="details-section">
          <div class="details-card">
            <div class="detail-item">
              <h4>{{ t("common.invoice-number") }}</h4>
              <p>{{ invoice.documentNumber }}</p>
            </div>
            <div class="detail-item">
              <h4>{{ t("common.invoice-date") }}</h4>
              <p>{{ dateStr }}</p>
            </div>
            <div class="detail-item">
              <h4>{{ t("common.subtotal") }}</h4>
              <p>{{ amount }}</p>
            </div>
            <div class="detail-item">
              <h4>{{ t("common.vat") }}</h4>
              <p>{{ vat }}</p>
            </div>
            <div class="detail-item">
              <h4>{{ t("common.total") }}</h4>
              <p>{{ total }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="action-section">
        <ion-button expand="block" size="large" class="primary-action" @click="download()">
          <font-awesome-icon :icon="['fas', 'download']" class="button-icon" />
          {{ t("common.download-pdf") }}
        </ion-button>
      </div>
    </div>
  </ion-content>
</template>

<script lang="ts">
import {
  IonContent,
  IonButton,
  modalController,
  useIonRouter,
} from "@ionic/vue";
import { defineComponent, PropType } from "vue";
import { Invoice } from "@/types/deals";
import { formatPrice, invoiceStatusDetails, paymentStatusDetails } from "@/utils";
import { DateTime } from "luxon";
import { Browser } from "@capacitor/browser";
import { useI18n } from "vue-i18n";
import localeService from "@/services/locale.service";

export default defineComponent({
  name: "InvoiceModal",
  components: {
    IonContent,
    IonButton,
  },
  props: {
    invoice: {
      type: Object as PropType<Invoice>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useIonRouter();
    return {
      t,
      dateStr: DateTime.fromISO(props.invoice.issuedAt)
        .setLocale(localeService.selectedLocale)
        .toLocaleString(DateTime.DATE_FULL),
      status: invoiceStatusDetails(props.invoice.status),
      amount: formatPrice(props.invoice.totalBeforeTax, props.invoice.currency),
      paymentStatus: paymentStatusDetails(props.invoice.paymentStatus),
      subTotal: formatPrice(
        props.invoice.totalBeforeTax -
          Math.round(
            props.invoice.totalBeforeTax * (props.invoice.taxPercentage / 100)
          ),
        props.invoice.currency
      ),
      vat: formatPrice(
        Math.round(
          props.invoice.totalBeforeTax * (props.invoice.taxPercentage / 100)
        ),
        props.invoice.currency
      ),
      total: formatPrice(
        props.invoice.totalBeforeTax +
          Math.round(
            props.invoice.totalBeforeTax * (props.invoice.taxPercentage / 100)
          ),
        props.invoice.currency
      ),
      download: () => {
        Browser.open({
          url: props.invoice.media.readPublicLink,
        });
      },
      navigateToDealDetails: () => {
        modalController.dismiss();
        router.push(
          `/tabs/deals/details/${props.invoice.deal["@id"].split("/")[2]}`
        );
      },
    };
  },
});
</script>
<style scoped>
/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
}

.scrollable-content {
  flex: 1;
  padding: 10px;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.company-info {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--loopin-color-gs-30);
}

.company-logo {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-logo-placeholder {
  width: 100%;
  height: 100%;
  background: var(--loopin-color-gs-30);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--loopin-color-gs-60);
  font-size: 20px;
}

.company-details {
  flex: 1;
}

.company-name {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-100);
  cursor: pointer;
}

.company-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status Section */
.status-section {
  margin-bottom: 24px;
}

.status-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 12px;
  background: var(--loopin-item-background);
  border-left: 4px solid var(--loopin-color-gs-40);
  transition: all 0.2s ease;
}

.status-card--gray {
  border-left-color: var(--loopin-color-gs-60);
  background: linear-gradient(135deg, rgba(128, 128, 128, 0.1), var(--loopin-item-background));
}

.status-card--orange {
  border-left-color: var(--loopin-color-orange);
  background: linear-gradient(135deg, rgba(255, 165, 0, 0.1), var(--loopin-item-background));
}

.status-card--green {
  border-left-color: var(--loopin-color-green);
  background: linear-gradient(135deg, rgba(0, 128, 0, 0.1), var(--loopin-item-background));
}

.status-card--red {
  border-left-color: var(--loopin-color-red);
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.1), var(--loopin-item-background));
}

.status-card--blue {
  border-left-color: var(--loopin-color-blue);
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1), var(--loopin-item-background));
}

.status-card--pink {
  border-left-color: var(--loopin-color-pink);
  background: linear-gradient(135deg, rgba(255, 105, 180, 0.1), var(--loopin-item-background));
}

.status-card--yellow {
  border-left-color: var(--loopin-color-yellow);
  background: linear-gradient(135deg, rgba(255, 255, 0, 0.1), var(--loopin-item-background));
}

.status-content {
  flex: 1;
}

.status-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--loopin-color-gs-100);
}

.status-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.details-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--loopin-color-gs-30);
}

.detail-item {
  margin-bottom: 20px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item h4 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: var(--loopin-color-gs-90);
}

.detail-item p {
  font-size: 15px;
  line-height: 1.5;
  color: var(--loopin-color-gs-70);
  margin: 0;
  font-weight: 600;
}

/* Action Section */
.action-section {
  padding: 20px;
}

.primary-action {
  --background: var(--loopin-color-blue);
  --background-activated: var(--loopin-color-blue);
  --color: white;
  --border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transition: all 0.2s ease;
}

.primary-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.button-icon {
  margin-right: 8px;
  font-size: 14px;
}

/* Modal Content */
.modal-content {
  --background: var(--loopin-color-gs-10);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .company-name {
    font-size: 18px;
  }
  
  .details-card {
    padding: 16px;
  }
  
  .action-section {
    padding: 16px 10px;
  }
}
</style>